import React, { useContext, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button, CircleX, Trash2 } from "../../ui";
import "./UploadCRM.css";
import { bulkCreateCRMAPI } from "../../../utils/api/crm";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";

export default function UploadCRMModal({ onClose, onUploaded }) {
  const [rows, setRows] = useState([]);
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const [filters, setFilters] = useState({
    businessName: "",
    city: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      validateAndSet(json);
    };

    reader.readAsArrayBuffer(file);
  };
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      return (
        (!filters.businessName ||
          r.businessName
            ?.toLowerCase()
            .includes(filters.businessName.toLowerCase())) &&
        (!filters.city ||
          r.city?.toLowerCase().includes(filters.city.toLowerCase())) &&
        (!filters.contactName ||
          r.contactName
            ?.toLowerCase()
            .includes(filters.contactName.toLowerCase())) &&
        (!filters.contactPhone ||
          r.contactPhone?.includes(filters.contactPhone)) &&
        (!filters.contactEmail ||
          r.contactEmail
            ?.toLowerCase()
            .includes(filters.contactEmail.toLowerCase()))
      );
    });
  }, [rows, filters]);

  const validateAndSet = (data) => {
    // 1. normalize + remove invalid rows
    const cleaned = data
      .map((row) => ({
        businessName: row["Business Name"]?.trim(),
        contactName: row["Contact Name"]?.trim(),
        contactPhone: row["Contact Phone"]?.toString().trim(),
        contactEmail: row["Contact Email"]?.trim(),
        city: row["City"]?.trim(),
      }))
      .filter((r) => r.businessName); // hard requirement

    // 2. build frequency maps
    const freq = {
      business: new Map(),
      phone: new Map(),
      email: new Map(),
    };

    cleaned.forEach((r) => {
      freq.business.set(
        r.businessName,
        (freq.business.get(r.businessName) || 0) + 1,
      );
      freq.phone.set(r.contactPhone, (freq.phone.get(r.contactPhone) || 0) + 1);
      freq.email.set(r.contactEmail, (freq.email.get(r.contactEmail) || 0) + 1);
    });

    // 3. enrich rows with duplicate flags
    const enriched = cleaned.map((r) => ({
      ...r,
      dup: {
        businessName: freq.business.get(r.businessName) > 1,
        phone: freq.phone.get(r.contactPhone) > 1,
        email: freq.email.get(r.contactEmail) > 1,
      },
    }));

    setRows(enriched);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    // Bulk import is behind the same permission as the button that opens it (NA-010).
    if (!can("crm.viewSwitch.use")) return;
    try {
      const token = localStorage.getItem("auth-token");

      await bulkCreateCRMAPI(token, rows);

      onUploaded();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content large"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crm-upload-title"
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        <div className="modal-header">
          <div>
            <span className="upload-kicker">
              {lang === "ar" ? "استيراد جماعي" : "Bulk import"}
            </span>
            <h3 id="crm-upload-title">
              {lang === "ar" ? "رفع بيانات CRM" : "Upload CRM Data"}
            </h3>
          </div>
          <Button
            type="button"
            variant="neutral"
            appearance="ghost"
            size="sm"
            rounded
            icon={<CircleX size={18} />}
            onClick={onClose}
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
          />
        </div>

        <input
          type="file"
          accept=".xlsx, .xls, .csv"
          onChange={handleFileUpload}
        />

        {rows.length === 0 && (
          <div className="crm-upload-empty">
            <strong>
              {lang === "ar"
                ? "اختر ملف Excel أو CSV"
                : "Choose an Excel or CSV file"}
            </strong>
            <span>
              {lang === "ar"
                ? "ستظهر معاينة قابلة للمراجعة هنا قبل الرفع"
                : "A reviewable preview will appear here before anything is uploaded."}
            </span>
          </div>
        )}

        {/* PREVIEW TABLE */}
        {rows.length > 0 && (
          <table className="menu-table">
            <thead>
              <tr>
                <th>#</th>

                <th>
                  <div className="menu-table-header-search">
                    {lang === "ar" ? "العملاء" : "Business"}
                    <input
                      className="header-filter"
                      placeholder="filter..."
                      value={filters.businessName}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          businessName: e.target.value,
                        }))
                      }
                    />
                  </div>
                </th>

                <th>
                  <div className="menu-table-header-search">
                    {lang === "ar" ? "المدينة" : "City"}
                    <input
                      className="header-filter"
                      placeholder="filter..."
                      value={filters.city}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, city: e.target.value }))
                      }
                    />
                  </div>
                </th>

                <th>
                  <div className="menu-table-header-search">
                    {lang === "ar" ? "اسم جهة الاتصال" : "Contact Name"}
                    <input
                      className="header-filter"
                      placeholder="filter..."
                      value={filters.contactName}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          contactName: e.target.value,
                        }))
                      }
                    />
                  </div>
                </th>

                <th>
                  <div className="menu-table-header-search">
                    {lang === "ar" ? "رقم الجوال" : "Phone"}
                    <input
                      className="header-filter"
                      placeholder="filter..."
                      value={filters.contactPhone}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          contactPhone: e.target.value,
                        }))
                      }
                    />
                  </div>
                </th>

                <th>
                  <div className="menu-table-header-search">
                    {lang === "ar" ? "البريد الإلكتروني" : "Email"}
                    <input
                      className="header-filter"
                      placeholder="filter..."
                      value={filters.contactEmail}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          contactEmail: e.target.value,
                        }))
                      }
                    />
                  </div>
                </th>

                <th></th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>

                  <td className={r.dup.businessName ? "dup-cell" : ""}>
                    {r.businessName}
                  </td>

                  <td>{r.city}</td>

                  <td>{r.contactName}</td>

                  <td className={r.dup.phone ? "dup-cell" : ""}>
                    {r.contactPhone}
                  </td>

                  <td className={r.dup.email ? "dup-cell" : ""}>
                    {r.contactEmail}
                  </td>

                  <td>
                    <Button
                      type="button"
                      variant="danger"
                      appearance="ghost"
                      size="sm"
                      rounded
                      className="delete-btn"
                      icon={<Trash2 size={16} />}
                      onClick={() => removeRow(i)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="modal-actions">
          <Button
            type="button"
            variant="primary"
            appearance="filled"
            size="sm"
            onClick={handleUpload}
            disabled={rows.length === 0}
          >
            {lang === "ar" ? "رفع البيانات" : "Upload Data"}
          </Button>
        </div>
      </div>
    </div>
  );
}
